<script setup lang="ts">
import { OfferType } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { FLEX_PACKAGE_PRICING } from '@/constants/toplistingPromotion';
import { calculateDaysBetweenTwoDates } from '@/helpers/dateUtils';
import { formatNumberWithTwoDecimals } from '@/helpers/numberUtils';
import { InsertionStatus, ToplistingVariant } from '@/types';

export interface Props {
  pricePerDay: number | string;
  startDate: string | null;
  endDate: string | null;
  toplistingVariant: ToplistingVariant;
  isVisible: boolean;
  toplistingTypeLabel?: string;
  hasFlexAccess?: boolean;
  offerType?: OfferType;
  insertionStatus?: InsertionStatus;
  isNoPromotionSelected?: boolean;
  isTLAboContractActive?: boolean;
  isRegionalHighlight?: boolean;
  regionalHighlightDistrictLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
  toplistingTypeLabel: '',
  hasFlexAccess: false,
  offerType: undefined,
  insertionStatus: undefined,
  isNoPromotionSelected: false,
  isTLAboContractActive: false,
  isRegionalHighlight: false,
  regionalHighlightDistrictLabel: '',
});

const { locale, t } = useI18n();

const numberOfDays = computed(() => {
  if (!props.startDate || !props.endDate) {
    return 0;
  }
  const days = calculateDaysBetweenTwoDates(props.startDate, props.endDate);
  return days !== null ? days + 1 : 0;
});

const flexPackagePrice = computed(() => {
  // Only show flex package fee for SME/FLEX customers with valid offer type
  if (!props.hasFlexAccess || !props.offerType) {
    return 0;
  }

  // Only show flex package fee for new listings (not already published)
  // These statuses represent new listings that haven't been published yet
  const newListingStatuses = [
    InsertionStatus.Draft,
    InsertionStatus.PendingApproval,
    InsertionStatus.PendingRepublishApproval,
    InsertionStatus.Approved,
  ];

  const isNewListingPublication =
    props.insertionStatus && newListingStatuses.includes(props.insertionStatus);
  if (!isNewListingPublication) {
    return 0;
  }

  return props.offerType === OfferType.Rent ? FLEX_PACKAGE_PRICING.RENT : FLEX_PACKAGE_PRICING.BUY;
});

const promotionCost = computed(() => {
  if (isLifetime.value) {
    // For lifetime, use the price directly (it's already the total one-time price)
    return Number(props.pricePerDay) || 0;
  }
  if (typeof props.pricePerDay !== 'number' || !numberOfDays.value) {
    return 0;
  }
  return props.pricePerDay * numberOfDays.value;
});

const totalCost = computed(() => {
  return promotionCost.value + flexPackagePrice.value;
});

const isLifetime = computed(() => props.toplistingVariant === ToplistingVariant.Lifetime);
const isTopAbo = computed(() => props.toplistingVariant === ToplistingVariant.TopAbo);
const shouldDisplay = computed(() => {
  if (props.isRegionalHighlight) {
    return props.isVisible;
  }

  // When "no promotion" is selected, only show if there's a flex package price
  if (props.isNoPromotionSelected) {
    return flexPackagePrice.value > 0;
  }

  // Allow TopAbo display when isTLAboContractActive is true
  const isTopAboAllowed = isTopAbo.value && props.isTLAboContractActive;

  // TopAbo doesn't require pricePerDay since it's slot-based
  const hasPriceInfo = isTopAboAllowed || props.pricePerDay;

  // Normal case: show cost summary when visible, not TopAbo (unless allowed), has promotion price, and valid dates
  return (
    props.isVisible &&
    (!isTopAbo.value || isTopAboAllowed) &&
    hasPriceInfo &&
    (isLifetime.value || (props.startDate && props.endDate) || isTopAboAllowed)
  );
});

const formatUbmAmount = (value: number): string => formatNumberWithTwoDecimals(value, locale.value);

const durationText = computed(() => {
  if (numberOfDays.value === 1) {
    return t('toplisting.modal.durationLabel', {
      days: numberOfDays.value,
      price: formatUbmAmount(Number(props.pricePerDay)),
    });
  }
  return t('toplisting.modal.durationLabelPlural', {
    days: numberOfDays.value,
    price: formatUbmAmount(Number(props.pricePerDay)),
  });
});

const shouldShowFlexRow = computed(
  () => !props.isRegionalHighlight && props.hasFlexAccess && flexPackagePrice.value > 0,
);

const flexRowLabel = computed(() => t('toplisting.modal.flexLabel'));
const flexRowType = computed(() => t('toplisting.modal.flexType'));
const flexRowDuration = computed(() => t('toplisting.modal.flexDuration'));
const flexRowPrice = computed(() => formatUbmAmount(flexPackagePrice.value));

const promotionCostFormatted = computed(() => formatUbmAmount(promotionCost.value));
const totalCostFormatted = computed(() => formatUbmAmount(totalCost.value));

const topAboSpecialText = computed(() => t('toplisting.modal.TLAboDuration'));
</script>

<template>
  <div v-if="shouldDisplay" :class="$style.root">
    <div :class="$style.title">
      {{ t('toplisting.modal.costSummary') }}
    </div>

    <div :class="$style.content">
      <!-- Flex row when user has Flex access -->
      <div v-if="shouldShowFlexRow" :class="$style.row">
        <span :class="$style.label">
          {{ flexRowLabel }}
        </span>
        <span :class="$style.type">
          {{ flexRowType }}
        </span>
        <span :class="$style.middle">
          {{ flexRowDuration }}
        </span>
        <span :class="$style.price_item">CHF {{ flexRowPrice }}</span>
      </div>

      <!-- Regular visibility upgrade row (hidden when no promotion is selected) -->
      <div v-if="!isNoPromotionSelected" :class="$style.row">
        <span :class="$style.label">
          {{ t('toplisting.modal.visibilityUpgrade') }}
        </span>
        <span :class="$style.type">
          {{
            isRegionalHighlight
              ? t('toplisting.modal.options.REGIONAL_HIGHLIGHT.header')
              : toplistingTypeLabel
          }}
        </span>
        <!-- Show special text for TopAbo, duration text for others -->
        <span v-if="isRegionalHighlight" :class="$style.middle">
          {{
            t('toplisting.modal.regionalHighlight.usesSlot', {
              district: regionalHighlightDistrictLabel,
            })
          }}
        </span>
        <span v-else-if="isTopAbo" :class="$style.middle">
          {{ topAboSpecialText }}
        </span>
        <span v-else-if="!isLifetime" :class="$style.middle">
          {{ durationText }}
        </span>
        <!-- Slot-based upgrades do not show a price -->
        <span v-if="!isTopAbo && !isRegionalHighlight" :class="$style.price_item">
          CHF
          {{ promotionCostFormatted }}
        </span>
      </div>
    </div>

    <div v-if="shouldShowFlexRow && !isNoPromotionSelected" :class="$style.footer">
      <span :class="$style.total_label">{{ t('toplisting.modal.totalCost') }}</span>
      <span :class="$style.total_price"> CHF {{ totalCostFormatted }} </span>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/assets/mixins';

.root {
  margin-top: $space-150;
}

.title {
  @include heading-4;

  margin-bottom: $space-100;
  font-weight: 600;
  color: $color-deep-blue-100;
}

.content {
  margin-bottom: $space-75;
}

.row {
  display: flex;
  align-items: center;
  margin-bottom: $space-25;
  line-height: 1.2;

  &:last-child {
    margin-bottom: 0;
  }
}

.left {
  display: flex;
  flex: 0 0 auto;
  gap: $space-100;
  align-items: center;
}

.label {
  @include body-3;

  flex: 0 0 140px;
  font-weight: 400;
  color: $color-deep-blue-100;
}

.type {
  @include body-3;

  flex: 0 0 160px;
  font-weight: 600;
  color: $color-deep-blue-100;
}

.middle {
  @include body-3;

  flex: 1;
  padding: 0 $space-50;
  font-weight: 400;
  color: $color-deep-blue-100;
  white-space: nowrap;
}

.duration {
  @include body-3;

  font-weight: 400;
  color: $color-deep-blue-100;
}

.price_item {
  @include body-3;

  margin-left: auto;
  font-weight: 600;
  color: $color-deep-blue-100;
}

.footer {
  display: flex;
  gap: $space-100;
  align-items: center;
  justify-content: flex-end;
  margin-top: $space-75;
}

.total_label {
  @include body-3;

  font-weight: 600;
  color: $color-deep-blue-100;
}

.total_price {
  @include body-3;

  font-weight: 600;
  color: $color-deep-blue-100;
}
</style>
